import React, { useEffect, useState } from 'react';
import { Text } from '../../../../../../assets/css/components/admin/marketplace-to-marketplace/modals/ModalSelectStoreAndSection';
import { getProcessMonitorById } from '../../../../../../hooks/api/processMonitor';

export default function ViewProductsAfterToPost({ isLoad, data }) {
  const [processMonitorData, setProcessMonitorData] = useState(null);
  const [initialFeedStatus, setInitialFeedStatus] = useState(null);
  const idRequest = initialFeedStatus?.Feed || 'No disponible';
  const getStatusMessage = () => {
    // Si hay processMonitorData, verificamos el avance
    if (processMonitorData) {
      // Verificar si hay errores en el proceso
      if (processMonitorData.data?.status === "failed") {
        return {
          message: "❌ Error en la publicación",
          color: "#721c24"
        };
      }
      
      if (processMonitorData.advance < 60) {
        return {
          message: "⏳ Estamos publicando tus productos a Falabella",
          color: "#ffa500"
        };
      } else if (processMonitorData.advance >= 60 && processMonitorData.advance < 100) {
        return {
          message: "🔄 Se estan cargando tus productos y validando que no hayan errores",
          color: "#3498db"
        };
      } else if (processMonitorData.advance >= 100) {
        return {
          message: initialFeedStatus?.FailedRecords > 0 ? "❌ Error en la publicación" : "✅ Proceso completado exitosamente",
          color: initialFeedStatus?.FailedRecords > 0 ? "#721c24" : "#155724"
        };
      }
    }

    // Estado inicial o por defecto
    return {
      message: "⏳ Estamos publicando tus productos a Falabella",
      color: "#ffa500"
    };
  };

  useEffect(() => {
    let intervalId;
    const processMonitorId = data?.processMonitorId;
    
    // ✅ VALIDACIÓN MEJORADA
    if (!processMonitorId) {
      console.warn('No se encontró processMonitorId en los datos:', data);
      return;
    }
    
    // Resetear estados cuando cambia el processMonitorId
    setProcessMonitorData(null);
    setInitialFeedStatus(null);
    
    const fetchProcessMonitor = async () => {
      try {
        const response = await getProcessMonitorById(processMonitorId);
        
        // ✅ VALIDACIÓN DE RESPUESTA
        if (!response?.data) {
          console.error('Respuesta inválida del processMonitor:', response);
          return false;
        }
        
        setProcessMonitorData(response.data);
        
        // Si el proceso está completo (100%), detenemos el polling
        if (response.data.advance >= 100) {
          if (intervalId) {
            clearInterval(intervalId);
            setInitialFeedStatus(response.data.data?.resultProducts?.initialFeedStatus)
          }
          return false;
        }
        return true; // continuar polling si no está al 100%
      } catch (error) {
        console.error('Error fetching process monitor:', error);
        return false;
      }
    };

    const startPolling = async () => {
      const shouldContinue = await fetchProcessMonitor();
      if (shouldContinue && !intervalId) {
        intervalId = setInterval(fetchProcessMonitor, 5000);
      }
    };

    if (processMonitorId) {
      startPolling();
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [data]);

  const statusInfo = getStatusMessage();

  const decodeBase64 = (base64String) => {
    try {
      // Decodificar string base64 a texto
      const decodedString = atob(base64String);
      return decodedString;
    } catch (error) {
      console.error('Error al decodificar base64:', error);
      return null;
    }
  };

  const parseCSVErrors = (csvString) => {
    if (!csvString) return [];
    
    try {
      const lines = csvString.split('\n').filter(line => line.trim());
      if (lines.length < 2) return [];
      
      // Obtener headers (primera línea)
      const headers = lines[0].split(';').map(header => header.replace(/"/g, '').trim());
      
      // Parsear datos (resto de líneas)
      const errors = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(';').map(value => value.replace(/"/g, '').trim());
        const errorObj = {};
        
        headers.forEach((header, index) => {
          errorObj[header] = values[index] || '';
        });
        
        // Solo agregar si tiene un error real (no vacío y no es información adicional)
        if (errorObj.Error && errorObj.Error.trim() !== '' && !isAdditionalInfo(errorObj.Error)) {
          errors.push(errorObj);
        }
      }
      
      return errors;
    } catch (error) {
      console.error('Error al parsear CSV:', error);
      return [];
    }
  };

  const isAdditionalInfo = (errorText) => {
    // Lista de patrones que indican información adicional, no errores reales
    const additionalInfoPatterns = [
      /garantía/i,
      /garantizado/i,
      /defecto/i,
      /contacte a nuestro servicio/i,
      /servicio al cliente/i,
      /meses contra defectos/i,
      /fábrica/i,
      /presenta algún defecto/i,
      /por favor contacte/i,
      /nuestro servicio/i,
      /producto en cuestión/i,
      /todos nuestros productos/i
    ];
    
    return additionalInfoPatterns.some(pattern => pattern.test(errorText));
  };

  const extractSKU = (errorObj) => {
    // Intentar obtener SKU de diferentes campos
    let sku = errorObj.SellerSku || errorObj.ParentSku || errorObj.SKU || errorObj.sku || '';
    
    // Si no hay SKU en los campos específicos, intentar extraerlo del nombre
    if (!sku && errorObj.Name) {
      const nameMatch = errorObj.Name.match(/\[([^\]]+)\]/);
      if (nameMatch) {
        sku = nameMatch[1];
      }
    }
    
    // Si aún no hay SKU, intentar extraerlo del mensaje de error
    if (!sku && errorObj.Error) {
      const errorMatch = errorObj.Error.match(/SKU '([^']+)'/);
      if (errorMatch) {
        sku = errorMatch[1];
      }
    }
    
    // Si aún no hay SKU, buscar en otros campos que puedan contener el SKU
    if (!sku) {
      // Buscar en todos los campos del objeto por patrones de SKU
      const allFields = Object.values(errorObj).join(' ');
      const skuPatterns = [
        /\[([A-Z0-9\-_]+)\]/g,  // Patrón [SKU] con guiones y guiones bajos
        /([A-Z0-9\-_]{8,})/g,   // Patrón de SKU largo (8+ caracteres alfanuméricos con guiones)
        /(B\d{2}[A-Z0-9]+)/g,   // Patrón específico B00...
        /(AW-[A-Z0-9]+)/g,      // Patrón específico AW-...
        /([A-Z0-9]{10,})/g      // Patrón de SKU largo (10+ caracteres alfanuméricos)
      ];
      
      for (const pattern of skuPatterns) {
        const matches = allFields.match(pattern);
        if (matches && matches.length > 0) {
          // Tomar el primer match que parezca un SKU válido
          const validSku = matches.find(match => {
            const cleanMatch = match.replace(/[[\]]/g, '');
            return cleanMatch.length >= 6 && 
                   /^[A-Z0-9\-_]+$/.test(cleanMatch) &&
                   !cleanMatch.includes(' ') &&
                   !cleanMatch.includes('Error') &&
                   !cleanMatch.includes('invalid');
          });
          if (validSku) {
            sku = validSku.replace(/[[\]]/g, '');
            break;
          }
        }
      }
    }
    
    // Si aún no hay SKU, intentar extraer de campos adicionales comunes
    if (!sku) {
      const additionalFields = ['ProductId', 'ProductID', 'Id', 'ID', 'Code', 'ProductCode'];
      for (const field of additionalFields) {
        if (errorObj[field] && errorObj[field].trim() !== '') {
          const fieldValue = errorObj[field].trim();
          // Verificar si parece un SKU válido
          if (/^[A-Z0-9\-_]{6,}$/.test(fieldValue) && !fieldValue.includes(' ')) {
            sku = fieldValue;
            break;
          }
        }
      }
    }
    
    return sku || 'N/A';
  };

  const groupErrorsBySKU = (errors) => {
    if (!errors || errors.length === 0) return [];
    
    const groupedErrors = {};
    
    errors.forEach(error => {
      const sku = extractSKU(error);
      
      // Debug temporal - eliminar después
      if (sku === 'N/A') {
        console.log('Error con SKU N/A:', error);
        console.log('Campos disponibles en el error:', Object.keys(error));
        console.log('Valores de los campos:', Object.entries(error));
      }
      
      if (!groupedErrors[sku]) {
        groupedErrors[sku] = {
          sku: sku,
          name: error.Name || '',
          errors: []
        };
      }
      groupedErrors[sku].errors.push(error.Error);
    });
    
    return Object.values(groupedErrors);
  };

  const getErrorSummary = (errors) => {
    if (!errors || errors.length === 0) return null;
    
    const groupedErrors = groupErrorsBySKU(errors);
    const errorTypes = {};
    
    groupedErrors.forEach(group => {
      group.errors.forEach(errorMsg => {
        errorTypes[errorMsg] = (errorTypes[errorMsg] || 0) + 1;
      });
    });
    
    return errorTypes;
  };

  const copyError = (errorsBase64) => {
    navigator.clipboard.writeText(decodeBase64(errorsBase64) || '');
    alert('Has copiado el error al portapapeles');
  }

  return (
    <div style={{ width: "100%", padding: "20px", textAlign: "center" }}>
      <div style={{ 
        borderRadius: "8px",
        padding: "0 30px",
        margin: "30px auto",
        maxWidth: 1200,
        textAlign: "left"
      }}>
        <Text size='18pt' fw='600' style={{ 
          color: statusInfo.color,
          marginBottom: "10px",
          display: "block",
          textAlign: "center"
        }}>
          {statusInfo.message}
        </Text>
        
        {processMonitorData?.advance >= 60 && processMonitorData?.advance < 100 && (
          <p style={{
            color: "#856404",
            backgroundColor: "#fff3cd",
            padding: "10px",
            borderRadius: "4px",
            margin: "0 auto 20px auto",
            maxWidth: "600px",
            textAlign: "center",
            fontSize: "14px"
          }}>
            <span role="img" aria-label="advertencia">⚠️</span> Si tu producto no cumple todos los campos obligatorios será rechazado
          </p>
        )}

        {/* Mostrar errores del proceso cuando falla */}
        {isLoad && processMonitorData?.data?.status === "failed" && (
          <div style={{
            backgroundColor: "#f8f9fa",
            border: "1px solid #dee2e6",
            borderRadius: "6px",
            padding: "15px",
            fontSize: "12px",
            maxWidth: 600,
            margin: "auto"
          }}>

            {processMonitorData.data?.errors && processMonitorData.data.errors.length > 0 && (
              <div style={{ marginBottom: "20px" }}>
                <h4 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "10px", color: "#dc3545" }}>
                  Detalles del error:
                </h4>
                <div style={{
                  padding: "10px",
                  backgroundColor: "#f8d7da",
                  border: "1px solid #f5c6cb",
                  borderRadius: "4px"
                }}>
                  {processMonitorData.data.errors.map((error, index) => (
                    <p key={index} style={{ margin: "0 0 5px 0", wordBreak: 'break-word' }}>
                      • {error}
                    </p>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px" }}>
              <div style={{ padding: "10px", backgroundColor: "#e8f5e8", borderRadius: "4px" }}>
                <strong>Productos publicados:</strong> {processMonitorData.data?.publishedProducts || 0}
              </div>
              <div style={{ padding: "10px", backgroundColor: "#ffeaa7", borderRadius: "4px" }}>
                <strong>Estado:</strong> Fallido
              </div>
            </div>
          </div>
        )}

        {/* Mostrar resultados exitosos */}
        {isLoad && processMonitorData?.advance === 100 && processMonitorData?.data?.status !== "failed" && (
          <div style={{
            backgroundColor: "#f8f9fa",
            border: "1px solid #dee2e6",
            borderRadius: "6px",
            padding: "15px",
            fontSize: "12px",
            maxWidth: 600,
            margin: "auto"
          }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}>
              <label style={{ fontSize: "16px", fontWeight: "600", marginRight: "10px" }}>ID de Solicitud: {idRequest}</label>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px", marginBottom: "20px" }}>
              <div style={{ padding: "10px", backgroundColor: "#e8f5e8", borderRadius: "4px" }}>
                <strong>Exitosos:</strong> {(initialFeedStatus?.TotalRecords || 0) - (initialFeedStatus?.FailedRecords || 0)}
              </div>
              <div style={{ padding: "10px", backgroundColor: "#ffeaa7", borderRadius: "4px" }}>
                <strong>Fallidos:</strong> {initialFeedStatus?.FailedRecords || 0}
              </div>
            </div>

            {initialFeedStatus?.FailureReports?.File?.length > 0 && (() => {
              const csvData = decodeBase64(initialFeedStatus?.FailureReports?.File);
              const parsedErrors = parseCSVErrors(csvData);
              const groupedErrors = groupErrorsBySKU(parsedErrors);
              const errorSummary = getErrorSummary(parsedErrors);
              
              return (
                <div style={{ marginBottom: "20px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                    <h3 style={{ fontSize: "16px", fontWeight: "600", margin: 0, color: "#dc3545" }}>
                      Errores encontrados ({groupedErrors.length} productos)
                    </h3>
                    <button
                      onClick={() => copyError(initialFeedStatus?.FailureReports?.File)}
                      style={{
                        padding: "8px 12px",
                        backgroundColor: "#007bff",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "12px"
                      }}
                    >
                      <span role="img" aria-label="portapapeles">📋</span> Copiar datos completos
                    </button>
                  </div>

                  {/* Resumen de tipos de errores */}
                  {errorSummary && (
                    <div style={{ marginBottom: "20px" }}>
                      <h4 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "10px", color: "#495057" }}>
                        Resumen de errores:
                      </h4>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                        {Object.entries(errorSummary).map(([errorType, count]) => (
                          <span
                            key={errorType}
                            style={{
                              padding: "6px 12px",
                              backgroundColor: "#e9ecef",
                              borderRadius: "20px",
                              fontSize: "12px",
                              fontWeight: "500",
                              color: "#495057",
                              border: "1px solid #dee2e6"
                            }}
                          >
                            {errorType} ({count})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Lista agrupada de errores */}
                  <div style={{
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #dee2e6",
                    borderRadius: "6px",
                    padding: "15px"
                  }}>
                    {groupedErrors.map((group, index) => (
                      <div key={index} style={{
                        padding: "12px",
                        backgroundColor: "#fff",
                        border: "1px solid #e9ecef",
                        borderRadius: "4px",
                        marginBottom: index < groupedErrors.length - 1 ? "10px" : "0",
                        fontSize: "14px"
                      }}>
                        <div style={{ color: "#6c757d", fontSize: "13px", marginBottom: "8px" }}>
                          <strong>SKU:</strong> {group.sku}
                        </div>
                        {group.errors.map((errorMsg, errorIndex) => (
                          <div key={errorIndex} style={{ 
                            color: "#dc3545", 
                            fontWeight: "500", 
                            marginBottom: errorIndex < group.errors.length - 1 ? "5px" : "0",
                            fontSize: "14px"
                          }}>
                            • {errorMsg}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}
